<template>
  <VanPopup
    v-model:show="store.edit.showGenerate"
    closeable
    class="w-[80%] max-w-[400px] max-h-[90%]"
    round
  >
    <div
      class="bg-[#2f2f2f] py-10 w-full flex flex-col justify-center items-center"
    >
      <div>
        <h2 class="text-lg mb-2">{{ t(`story.g.title`) }}</h2>
        <div class="mb-2">
          <VanButton
            type="primary"
            size="small"
            round
            @click="checkAll"
            class="mr-2"
          >
            {{ t("Buttons.selectAll") }}
          </VanButton>

          <VanButton type="primary" size="small" round @click="checkReverse">
            {{ t("Buttons.deselectAll") }}
          </VanButton>
        </div>

        <VanCheckboxGroup
          v-model="store.edit.generateChecked"
          class="flex flex-col"
          ref="checkboxGroup"
        >
          <VanCheckbox
            v-for="item in list"
            :name="item"
            class="my-1"
            :disabled="item === 'type' || item === store.edit.generateTarget"
          >
            {{ t(`story.g.${item}`) }}
          </VanCheckbox>

          <VanCheckbox
            v-for="(item, idx) in contentNodeList"
            :name="item"
            class="my-1"
            :disabled="item === 'node-' + store.edit.generateContentNodeIdx"
          >
            {{ t(`story.setting.node`) }} {{ idx + 1 }}
          </VanCheckbox>

          <VanCheckbox
            v-for="(item, idx) in momentList"
            :name="item"
            class="my-1 whitespace-nowrap overflow-hidden max-w-[200px]"
          >
            {{ t("story.tab.moment") }}{{ idx + 1 }}:
            {{ momentStore.momentMap[item.split("-")[1]].text }}
          </VanCheckbox>
        </VanCheckboxGroup>

        <p class="text-base-content/50 text-xs my-2" v-if="list.length <= 6">
          {{ t("Texts.afterCompletingFromText") }}
        </p>

        <div class="flex items-center my-4 justify-evenly">
          <button class="btn btn-primary" @click="clickAutoGenerate">
            {{ t(`story.auto`) }}
          </button>
          <button class="btn" @click="store.edit.showGenerate = false">
            {{ t(`story.g.cancel`) }}
          </button>
        </div>
      </div>
    </div>
  </VanPopup>

  <VanDialog v-model:show="store.edit.showGenerateLoadingModal">
    <div class="flex flex-col gap-4 py-4 px-6">
      <VanField
        v-model="store.edit.generateContent"
        rows="1"
        :autosize="{ minHeight: 130, maxHeight: 400 }"
        type="textarea"
        class="mt-2.5 text-[#ABABAB]"
      />
    </div>

    <template #footer>
      <div class="flex justify-center py-4">
        <div v-if="store.edit.isGenerating" class="flex items-center">
          <VanLoading class="mr-3" color="#fff" />
          <button class="btn btn-sm" @click="clickStop">
            {{ t("Buttons.stop") }}
          </button>
        </div>
        <template v-else>
          <button class="btn btn-primary btn-sm mr-2" @click="clickInsert">
            {{ t("Buttons.insert") }}
          </button>

          <button
            class="btn btn-sm"
            @click="store.edit.showGenerateLoadingModal = false"
          >
            {{ t(`story.g.cancel`) }}
          </button>
        </template>
      </div>
    </template>
  </VanDialog>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { useStoryBook } from "@/stores/story-book";
import { watch, ref } from "vue";
import api from "@/modules/api";
import { CheckboxGroupInstance } from "vant/es";
import { useMoment } from "@/stores/moment";
import { useGlobalStream } from "@/stores/globalStream";
const { t } = useI18n();
const store = useStoryBook();
const momentStore = useMoment();
const checkboxGroup = ref<CheckboxGroupInstance | null>(null);

const keys: Array<StoryGenerateParamsKey> = [
  "characterzz",
  "description",
  "exampleText",
  "beginning",
  "ending",
  "previousStoryId",
];

const list = ref<Array<StoryGenerateParamsKey | string>>([]);

const contentNodeList = ref<string[]>([]);
const momentList = ref<string[]>([]);

const clickAutoGenerate = async () => {
  const { edit, form } = store;
  const {
    generateChecked,
    generateTarget,
    generateContentNodeIdx,
    generateContentNodeTarget,
  } = edit;
  edit.showGenerate = false;
  const params: StoryGenerateParams = {};
  let nodeMap: Record<number, StoryContentNode> = {};
  generateChecked
    .sort((a: any, b: any) => a - b)
    .forEach((v) => {
      if (typeof v === "string" && v.startsWith("node-")) {
        const [_, nodeIdx] = v.split("-");
        if (!params.bodyNodezz) params.bodyNodezz = [];
        const node = { ...form.bodyNodezz![Number(nodeIdx)] };
        nodeMap[Number(nodeIdx)] = node;
        params.bodyNodezz.push(node);
        return;
      } else if (typeof v === "string" && v.startsWith("moment-")) {
        const [_, momentId] = v.split("-");
        if (!params.momentIdzz) params.momentIdzz = [];
        params.momentIdzz.push(Number(momentId));
        return;
      } else {
        params[v] = form[v] as any;
      }
    });

  if (generateTarget !== "bodyNodezz") {
    params[generateTarget] += `"""${t("Texts.completeContent")}"""` as any;
  } else {
    nodeMap[generateContentNodeIdx][generateContentNodeTarget] += `"""${t(
      "Texts.completeContent"
    )}"""`;
  }
  edit.showGenerateLoadingModal = true;

  edit.isGenerating = true;
  edit.generateContent = "";
  try {
    api.storyWriteGenerate(params, callback);
    function callback(text: any) {
      if (typeof text === "object") {
        edit.generateContent = text.data.text;
        edit.isGenerating = false;
      } else edit.generateContent += text;
    }

    // 自动添加换行的逻辑
    // if (generateTarget !== "bodyNodezz") {
    //   if (
    //     form[generateTarget] &&
    //     !(form[generateTarget] as string)?.endsWith("\n")
    //   )
    //     (form[generateTarget] as string) += "\n\n";
    // } else {
    //   if (
    //     form.bodyNodezz![generateContentNodeIdx][generateContentNodeTarget] &&
    //     !form.bodyNodezz![generateContentNodeIdx][
    //       generateContentNodeTarget
    //     ].endsWith("\n")
    //   ) {
    //     form.bodyNodezz![generateContentNodeIdx][generateContentNodeTarget] +=
    //       "\n\n";
    //   }
    // }
  } catch (e) {
    console.log(e);
  }
};

const clickInsert = () => {
  const { edit, form } = store;
  const {
    generateTarget,
    generateContentNodeIdx,
    generateContentNodeTarget,
    generateContent,
  } = edit;
  if (generateTarget !== "bodyNodezz") {
    form[generateTarget] += generateContent as any;
  } else {
    form.bodyNodezz![generateContentNodeIdx][generateContentNodeTarget] +=
      generateContent;
  }
  edit.showGenerateLoadingModal = false;
};

const clickStop = () => {
  useGlobalStream().stopStream();
  store.edit.isGenerating = false;
};

const checkAll = () => {
  checkboxGroup.value!.toggleAll(true);
};

const checkReverse = () => {
  checkboxGroup.value!.toggleAll({ checked: false, skipDisabled: true });
};

watch(
  () => store.edit.showGenerate,
  (v) => {
    if (v) {
      const { edit, form } = store;
      const { generateTarget, generateContentNodeIdx } = edit;
      list.value = ["type"];
      store.edit.generateChecked = ["type"];
      if (generateTarget !== "bodyNodezz") {
        store.edit.generateChecked.push(generateTarget);
        list.value.push(generateTarget);
      } else {
        store.edit.generateChecked.push("node-" + generateContentNodeIdx);
      }

      // 构建自动生成列表
      keys.forEach((v) => {
        if (
          v === generateTarget || // 选中的目标
          (Array.isArray(form[v]) && (form[v] as any).length === 0) || // 数组为空
          !form[v] // 字符串为空
        )
          return;
        list.value.push(v);
      });

      // 构建内容节点列表
      contentNodeList.value = [];
      form.bodyNodezz!.forEach((v, i) => {
        // if (v.beginning || v.body || v.ending)
        contentNodeList.value.push("node-" + i);
      });
      momentList.value = [];
      form.momentIdzz!.forEach((v) => momentList.value.push("moment-" + v));
    }
  }
);
</script>
